// =============================================
// Parent Dashboard - Home Screen
// Kipikid - מערכת נוכחות ילדים בגנים
// =============================================

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle2,
  Clock,
  AlertTriangle,
  ChevronLeft,
  MapPin,
  Thermometer,
  Sun,
  MessageSquare,
  Loader2,
  Bell,
  XCircle,
} from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useSimulationStore } from '../../stores/simulationStore';
import { attendanceApi, syncApi, parentApi, notificationsApi, UserNotification, Child, MarkAttendanceRequest } from '../../services/api';
import ChildAvatar from '../../components/common/ChildAvatar';

// Helper: get cached parent photo from localStorage (cached as base64 by ParentChildren page)
const PARENT_PHOTO_PREFIX = 'kipikid-parent-photo-';

const getParentPhoto = (publicId: string): string | null => {
  return localStorage.getItem(`${PARENT_PHOTO_PREFIX}${publicId}`);
};

const saveParentPhoto = (childPublicId: string, base64: string): void => {
  try {
    localStorage.setItem(`${PARENT_PHOTO_PREFIX}${childPublicId}`, base64);
  } catch {
    // If localStorage full, clean up oldest 30% of photos
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(PARENT_PHOTO_PREFIX)) keysToRemove.push(key);
    }
    const toRemove = Math.ceil(keysToRemove.length * 0.3);
    for (let i = 0; i < toRemove; i++) localStorage.removeItem(keysToRemove[i]);
  }
};

/** Download photo from SAS URL and cache as base64 in localStorage */
const downloadAndCachePhoto = async (childPublicId: string, sasUrl: string): Promise<string | null> => {
  try {
    const response = await fetch(sasUrl);
    if (!response.ok) return null;
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        saveParentPhoto(childPublicId, base64);
        resolve(base64);
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

/** Cache all children's photos — downloads from SAS URL only if not already cached */
const cacheChildrenPhotos = async (children: { childPublicId: string; childPhotoUrl?: string }[]): Promise<Record<string, string>> => {
  const photoMap: Record<string, string> = {};
  await Promise.all(children.map(async (child) => {
    if (!child.childPhotoUrl) return;
    const cached = getParentPhoto(child.childPublicId);
    if (cached) { photoMap[child.childPublicId] = cached; return; }
    const base64 = await downloadAndCachePhoto(child.childPublicId, child.childPhotoUrl);
    if (base64) photoMap[child.childPublicId] = base64;
  }));
  return photoMap;
};

// Helper: get child's arrival time for today based on day of week
// Same logic as ParentChildren.tsx getTodayArrivalTime
const getTodayArrivalTime = (
  sun?: string | null, mon?: string | null, tue?: string | null,
  wed?: string | null, thu?: string | null, fri?: string | null
): string | null => {
  const day = new Date().getDay(); // 0=Sun,1=Mon,...
  switch (day) {
    case 0: return sun || null;
    case 1: return mon || null;
    case 2: return tue || null;
    case 3: return wed || null;
    case 4: return thu || null;
    case 5: return fri || null;
    default: return null;
  }
};

// Types
interface ChildData {
  id: number;
  publicId: string;
  firstName: string;
  lastName: string;
  photoUrl?: string;
  // Note: Using only kindergartenPublicId for security (no internal Id)
  kindergartenPublicId: string;
  kindergartenName: string;
  arrivedAt?: string;
  isExpectedToday: boolean;
  isOnVacationToday?: boolean;
  vacationReason?: number; // 1=חופשה, 2=מחלה, 3=אחר
}

// Styles
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  heroCard: {
    background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
    borderRadius: '14px',
    padding: '10px 14px',
    color: 'white',
    boxShadow: '0 4px 15px rgba(20, 184, 166, 0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  heroLeft: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  heroGreeting: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    opacity: 0.9,
  },
  heroTitle: {
    fontSize: '13px',
    fontWeight: '600',
    marginTop: '2px',
  },
  statsRow: {
    display: 'flex',
    gap: '8px',
  },
  statCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    borderRadius: '10px',
    padding: '6px 14px',
    backdropFilter: 'blur(8px)',
    textAlign: 'center' as const,
    minWidth: '60px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  },
  statLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    fontSize: '10px',
    opacity: 0.95,
  },
  statValue: {
    fontSize: '18px',
    fontWeight: 'bold',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: 'bold',
    color: '#1f2937',
  },
  sectionLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: '#f97316',
    fontSize: '13px',
    fontWeight: '500',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
  },
  childrenRow: {
    display: 'flex',
    gap: '8px',
    overflowX: 'auto' as const,
  },
  childCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '10px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    minWidth: '130px',
    flex: '1 1 0',
  },
  childPhoto: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    objectFit: 'cover' as const,
    border: '2px solid',
  },
  childInfo: {
    textAlign: 'center' as const,
  },
  childName: {
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: '1px',
  },
  childKindergarten: {
    fontSize: '11px',
    color: '#6b7280',
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    borderRadius: '8px',
    fontSize: '10px',
    fontWeight: '500',
  },
  actionsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
  },
  actionCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '6px',
    padding: '14px 12px',
    borderRadius: '14px',
    border: 'none',
    cursor: 'pointer',
    transition: 'transform 0.2s',
  },
  actionLabel: {
    fontSize: '12px',
    fontWeight: '500',
  },
  messageCard: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '16px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    display: 'flex',
    gap: '12px',
  },
  messageIcon: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    backgroundColor: '#ccfbf1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  messageContent: {
    flex: 1,
  },
  messageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px',
  },
  messageSender: {
    fontWeight: '500',
    color: '#1f2937',
  },
  messageTime: {
    fontSize: '12px',
    color: '#9ca3af',
  },
  messageText: {
    fontSize: '14px',
    color: '#6b7280',
    lineHeight: 1.5,
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '24px',
    padding: '24px',
    maxWidth: '400px',
    width: '100%',
    maxHeight: '80vh',
    overflow: 'auto',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: '16px',
    textAlign: 'center' as const,
  },
  modalChildItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    borderRadius: '12px',
    marginBottom: '8px',
    cursor: 'pointer',
    border: '2px solid #e5e7eb',
    transition: 'all 0.2s',
  },
  modalButton: {
    width: '100%',
    padding: '14px',
    borderRadius: '12px',
    border: 'none',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '16px',
  },
};

// Colors for modals
const modalColors = {
  primary: '#f97316',
  primaryLight: '#ffedd5',
  success: '#22c55e',
  successLight: '#dcfce7',
  warning: '#f59e0b',
  warningLight: '#fef3c7',
  danger: '#ef4444',
  dangerLight: '#fee2e2',
  text: '#1f2937',
  textLight: '#6b7280',
  border: '#e5e7eb',
};

export default function ParentDashboard() {
  const navigate = useNavigate();
  const { user, token } = useAuthStore();
  const [children, setChildren] = useState<ChildData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showMarkModal, setShowMarkModal] = useState(false);
  const [selectedChild, setSelectedChild] = useState<ChildData | null>(null);
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recentMessages, setRecentMessages] = useState<UserNotification[]>([]);

  // Message modal state
  const [messageModal, setMessageModal] = useState<{
    show: boolean;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
  } | null>(null);

  const showMessage = (type: 'success' | 'error' | 'warning' | 'info', title: string, message: string) => {
    setMessageModal({ show: true, type, title, message });
  };

  const closeMessage = () => {
    setMessageModal(null);
  };

  // Get greeting based on time (use simulation time if active)
  const hour = useSimulationStore.getState().getDisplayTime().getHours();
  const greeting = hour < 12 ? 'בוקר טוב' : hour < 17 ? 'צהריים טובים' : 'ערב טוב';

  // Hebrew relative time helper
  const timeAgo = (dateStr: string): string => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMinutes < 1) return 'עכשיו';
    if (diffMinutes < 60) return `לפני ${diffMinutes} דקות`;
    if (diffHours === 1) return 'לפני שעה';
    if (diffHours < 24) return `לפני ${diffHours} שעות`;
    if (diffDays === 1) return 'אתמול';
    if (diffDays < 7) return `לפני ${diffDays} ימים`;
    if (diffDays < 30) return `לפני ${Math.floor(diffDays / 7)} שבועות`;
    return date.toLocaleDateString('he-IL');
  };

  // Load children data + recent messages
  useEffect(() => {
    loadChildren();
    loadRecentMessages();
  }, []);

  // Attendance status polling — refresh every 60s to show real-time arrivals
  useEffect(() => {
    if (children.length === 0) return;

    const fetchAttendance = async () => {
      const childPublicIds = children.filter(c => c.publicId && !c.publicId.startsWith('mock-')).map(c => c.publicId);
      if (childPublicIds.length === 0) return;
      try {
        // Batch endpoint: single API call for all children
        const result = await attendanceApi.getChildrenStatusBatch(childPublicIds).catch((err) => {
          console.warn(`[Dashboard] batch status failed, falling back to individual:`, err?.message);
          return null;
        });

        let anyChanged = false;
        const updates: Record<string, string> = {};

        if (result?.success && result.data) {
          for (const record of result.data) {
            if (record.arrivedAt && record.childPublicId) {
              const raw = record.arrivedAt;
              const match = raw.match(/T(\d{2}:\d{2})/);
              const timeStr = match ? match[1] : raw.substring(0, 5);
              if (timeStr !== '00:00') { // Skip MinValue placeholder
                updates[record.childPublicId] = timeStr;
              }
            }
          }
        } else {
          // Fallback to individual calls if batch fails
          const statuses = await Promise.all(
            childPublicIds.map(id =>
              attendanceApi.getChildStatus(id).catch(() => null)
            )
          );
          childPublicIds.forEach((id, i) => {
            const res = statuses[i];
            if (res?.success && res.data?.arrivedAt) {
              const raw = res.data.arrivedAt;
              const match = raw.match(/T(\d{2}:\d{2})/);
              const timeStr = match ? match[1] : raw.substring(0, 5);
              updates[id] = timeStr;
            }
          });
        }

        setChildren(prev => {
          const next = prev.map(c => {
            if (c.publicId && updates[c.publicId] && c.arrivedAt !== updates[c.publicId]) {
              anyChanged = true;
              return { ...c, arrivedAt: updates[c.publicId] };
            }
            return c;
          });
          return anyChanged ? next : prev;
        });
      } catch (err) {
        console.error('[Dashboard] Error fetching attendance:', err);
      }
    };

    fetchAttendance(); // Immediate first fetch
    const interval = setInterval(fetchAttendance, 60_000); // Every 60 seconds
    return () => clearInterval(interval);
  }, [children]);

  const loadRecentMessages = async () => {
    try {
      const response = await notificationsApi.getNotifications(1, 5);
      if (response.success && response.data) {
        const msgs = response.data
          .filter((n: UserNotification) => n.type === 'KindergartenMessage')
          .slice(0, 2);
        setRecentMessages(msgs);
      }
    } catch (err) {
      console.error('Failed to load recent messages:', err);
    }
  };

  const loadChildren = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to load from API first
      const response = await parentApi.getMyChildren();

      if (response.success && response.data && response.data.length > 0) {
        // Download and cache photos as base64 in localStorage (runs in background)
        const photoMap = await cacheChildrenPhotos(response.data);

        // Get existing localStorage data to preserve local modifications
        const existingData = JSON.parse(localStorage.getItem('kipikid-children') || '[]');

        // Map API response to dashboard format, merging with local data
        const dashboardChildren: ChildData[] = response.data.map((child, index) => {
          const existing = existingData.find((e: any) => e.publicId === child.childPublicId);
          return {
            id: index + 1,
            publicId: child.childPublicId,
            firstName: child.childFirstName,
            lastName: child.childLastName,
            // Priority: cached base64 → API photo (fresh SAS token) → existing local data
            photoUrl: photoMap[child.childPublicId] || child.childPhotoUrl || getParentPhoto(child.childPublicId) || existing?.photoUrl,
            kindergartenPublicId: child.kindergartenPublicId || existing?.kindergartenPublicId || '',
            // Keep local kindergarten name if changed
            kindergartenName: existing?.kindergartenName || child.kindergartenName,
            arrivedAt: undefined, // Will be updated from attendance API
            isExpectedToday: !child.isOnVacationToday,
            isOnVacationToday: child.isOnVacationToday,
            vacationReason: child.vacationReason,
          };
        });

        // Merge with localStorage - keep local modifications and local-only children
        const localOnlyChildren = existingData.filter((local: any) =>
          !response.data.some(api => api.childPublicId === local.publicId)
        );

        // Save merged data to localStorage - include isPayingParent for contacts page filtering
        // Always prefer API photo URL (has fresh SAS token)
        const storageData = [...response.data.map((child, index) => ({
          id: index + 1,
          publicId: child.childPublicId,
          firstName: child.childFirstName,
          lastName: child.childLastName,
          photoUrl: child.childPhotoUrl, // Always use API photo URL (fresh SAS token)
          kindergartenName: existingData.find((e: any) => e.publicId === child.childPublicId)?.kindergartenName || child.kindergartenName,
          expectedTime: getTodayArrivalTime(
            child.sundayArrivalTime, child.mondayArrivalTime, child.tuesdayArrivalTime,
            child.wednesdayArrivalTime, child.thursdayArrivalTime, child.fridayArrivalTime
          ) || existingData.find((e: any) => e.publicId === child.childPublicId)?.expectedTime || '08:30',
          kindergartenPublicId: child.kindergartenPublicId,
          isPayingParent: child.isPayingParent,
        })), ...localOnlyChildren.map((local: any) => ({
          ...local,
          isPayingParent: local.isPayingParent ?? true, // Default to true for local-only children
        }))];
        localStorage.setItem('kipikid-children', JSON.stringify(storageData));

        // Add local-only children to dashboard
        const allChildren = [...dashboardChildren, ...localOnlyChildren.map((local: any, idx: number) => ({
          id: dashboardChildren.length + idx + 1,
          publicId: local.publicId,
          firstName: local.firstName,
          lastName: local.lastName,
          photoUrl: local.photoUrl,
          kindergartenPublicId: local.kindergartenPublicId || '',
          kindergartenName: local.kindergartenName,
          arrivedAt: undefined,
          isExpectedToday: true,
        }))];

        setChildren(allChildren);
        return;
      }

      // Try to load from localStorage as fallback
      const storedChildren = localStorage.getItem('kipikid-children');
      if (storedChildren) {
        const parsed = JSON.parse(storedChildren);
        // Only use if it has real publicIds (not mock)
        if (parsed.length > 0 && parsed[0].publicId && !parsed[0].publicId.startsWith('mock-')) {
          const dashboardChildren: ChildData[] = parsed.map((child: any, index: number) => ({
            id: child.id || index + 1,
            publicId: child.publicId || `child-${index + 1}`,
            firstName: child.firstName,
            lastName: child.lastName,
            // Use base64 cached photo from ParentChildren page, or skip SAS URLs (they expire)
            photoUrl: getParentPhoto(child.publicId) || undefined,
            kindergartenPublicId: child.kindergartenPublicId || '',
            kindergartenName: child.kindergartenName,
            arrivedAt: child.arrivedAt,
            isExpectedToday: true,
          }));
          setChildren(dashboardChildren);
          return;
        }
      }

      // No children found - show empty state
      setChildren([]);
    } catch (err) {
      console.error('Dashboard: Error loading children:', err);
      // Try localStorage as last resort
      const storedChildren = localStorage.getItem('kipikid-children');
      if (storedChildren) {
        try {
          const parsed = JSON.parse(storedChildren);
          if (parsed.length > 0 && parsed[0].publicId && !parsed[0].publicId.startsWith('mock-')) {
            const dashboardChildren: ChildData[] = parsed.map((child: any, index: number) => ({
              id: child.id || index + 1,
              publicId: child.publicId,
              firstName: child.firstName,
              lastName: child.lastName,
              // Don't use cached photoUrl - SAS tokens expire after 24h
              photoUrl: undefined,
              kindergartenPublicId: child.kindergartenPublicId || '',
              kindergartenName: child.kindergartenName,
              arrivedAt: child.arrivedAt,
              isExpectedToday: true,
            }));
            setChildren(dashboardChildren);
            return;
          }
        } catch (e) {
          console.error('Error parsing localStorage:', e);
        }
      }
      setError('שגיאה בטעינת נתונים');
    } finally {
      setLoading(false);
    }
  };

  // סימון הגעה
  const handleMarkAttendance = async () => {
    // מציג את המודל לבחירת ילד
    const pendingChildren = children.filter(c => !c.arrivedAt && c.isExpectedToday);
    
    if (pendingChildren.length === 0) {
      showMessage('success', 'כל הכבוד!', 'כל הילדים כבר הגיעו היום!');
      return;
    }
    
    if (pendingChildren.length === 1) {
      // אם יש רק ילד אחד - סמן אותו ישירות
      setSelectedChild(pendingChildren[0]);
      await markChildArrival(pendingChildren[0]);
    } else {
      // אם יש כמה ילדים - הצג מודל לבחירה
      setShowMarkModal(true);
    }
  };

  const markChildArrival = async (child: ChildData) => {
    try {
      setMarking(true);
      setError(null);

      // קבלת מיקום (אופציונלי)
      let location: { latitude: number; longitude: number } | undefined;
      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout: 5000,
            enableHighAccuracy: true,
          });
        });
        location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
      } catch (geoError) {
        console.log('Location not available:', geoError);
        // ממשיכים בלי מיקום
      }

      const request: MarkAttendanceRequest = {
        childPublicId: child.publicId,
        kindergartenPublicId: child.kindergartenPublicId,
        source: 'ParentApp',
        location,
      };

      console.log('Marking attendance:', request);
      
      const response = await attendanceApi.markArrival(request);
      
      if (response.success) {
        // עדכון הסטטוס המקומי — use server time (supports simulation)
        // IMPORTANT: Don't use new Date() — it returns real time, not simulated time
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const responseAny = response as any;
        const markedAtRaw = responseAny.data?.markedAt || responseAny.data?.MarkedAt || responseAny.markedAt || responseAny.MarkedAt;
        let timeStr: string;
        if (markedAtRaw) {
          const match = String(markedAtRaw).match(/T(\d{2}:\d{2})/);
          timeStr = match ? match[1] : String(markedAtRaw).substring(11, 16);
        } else {
          const now = new Date();
          timeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
        }
        
        setChildren(prev => 
          prev.map(c => 
            c.publicId === child.publicId 
              ? { ...c, arrivedAt: timeStr }
              : c
          )
        );
        
        showMessage('success', 'נרשם בהצלחה', `${child.firstName} סומן/ה כהגיע/ה בשעה ${timeStr}`);
      } else {
        throw new Error(response.message || 'שגיאה בסימון הגעה');
      }
    } catch (err: any) {
      console.error('Error marking attendance:', err);
      setError(err.message || 'שגיאה בסימון הגעה');
      showMessage('error', 'שגיאה', 'שגיאה בסימון הגעה: ' + (err.message || 'נסה שוב'));
    } finally {
      setMarking(false);
      setShowMarkModal(false);
      setSelectedChild(null);
    }
  };

  // Stats
  const arrivedCount = children.filter(c => c.arrivedAt).length;
  const pendingCount = children.filter(c => !c.arrivedAt && c.isExpectedToday && !c.isOnVacationToday).length;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Loader2 className="animate-spin" size={40} color="#14b8a6" />
      </div>
    );
  }

  return (
    <div style={styles.container} dir="rtl">
      {/* Hero Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={styles.heroCard}
      >
        <div style={styles.heroLeft}>
          <div style={styles.heroGreeting}>
            <Sun size={14} />
            <span>{greeting}, {user?.name || user?.firstName || 'הורה'}</span>
          </div>
          <h2 style={styles.heroTitle}>סטטוס היום</h2>
        </div>
        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>
              <CheckCircle2 size={12} color="#86efac" />
              <span>הגיעו</span>
            </div>
            <p style={styles.statValue}>{arrivedCount}</p>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>
              <Clock size={12} color="#fcd34d" />
              <span>ממתינים</span>
            </div>
            <p style={styles.statValue}>{pendingCount}</p>
          </div>
        </div>
      </motion.div>
      
      {/* Error message */}
      {error && (
        <div style={{ 
          backgroundColor: '#fee2e2', 
          color: '#dc2626', 
          padding: '12px', 
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}
      
      {/* Children Section */}
      <section>
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}>הילדים שלי</h3>
          <button style={styles.sectionLink} onClick={() => navigate('/parent/children')}>
            הצג הכל
            <ChevronLeft size={14} />
          </button>
        </div>

        <div style={styles.childrenRow}>
          {children.map((child, index) => (
            <ChildStatusCard
              key={child.id}
              child={child}
              index={index}
              onClick={() => {
                if (!child.arrivedAt) {
                  setSelectedChild(child);
                  markChildArrival(child);
                }
              }}
            />
          ))}
        </div>
      </section>
      
      {/* Quick Actions */}
      <section>
        <h3 style={styles.sectionTitle}>פעולות מהירות</h3>
        <div style={{ ...styles.actionsGrid, marginTop: '10px' }}>
          <ActionButton
            icon={MapPin}
            label="סימון הגעה"
            bgColor="#dcfce7"
            iconColor="#16a34a"
            onClick={handleMarkAttendance}
            disabled={marking}
          />
          <ActionButton
            icon={Thermometer}
            label="דיווח על מחלה"
            bgColor="#fee2e2"
            iconColor="#dc2626"
            onClick={() => navigate('/parent/calendar')}
          />
          <ActionButton
            icon={MessageSquare}
            label="הודעות מהגן"
            bgColor="#ffedd5"
            iconColor="#ea580c"
            onClick={() => navigate('/parent/messages')}
          />
          <ActionButton
            icon={AlertTriangle}
            label="חופשה"
            bgColor="#fef3c7"
            iconColor="#d97706"
            onClick={() => navigate('/parent/calendar')}
          />
        </div>
      </section>
      
      {/* Recent Messages */}
      <section>
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}>הודעות אחרונות</h3>
          <button style={styles.sectionLink} onClick={() => navigate('/parent/messages')}>
            הצג הכל
            <ChevronLeft size={16} />
          </button>
        </div>

        {recentMessages.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ ...styles.messageCard, justifyContent: 'center', padding: '24px 16px' }}
          >
            <p style={{ color: '#9ca3af', fontSize: '14px', textAlign: 'center' as const, margin: 0 }}>
              אין הודעות חדשות
            </p>
          </motion.div>
        ) : (
          recentMessages.map((msg, idx) => (
            <motion.div
              key={msg.publicId || idx}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              style={{ ...styles.messageCard, marginBottom: idx < recentMessages.length - 1 ? '8px' : '0' }}
              onClick={() => navigate('/parent/messages')}
            >
              <div style={styles.messageIcon}>
                <MessageSquare size={20} color="#0d9488" />
              </div>
              <div style={styles.messageContent}>
                <div style={styles.messageHeader}>
                  <span style={styles.messageSender}>{msg.metadata?.kindergartenName || msg.title || 'הודעה'}</span>
                  <span style={styles.messageTime}>{timeAgo(msg.createdAt)}</span>
                </div>
                <p style={styles.messageText}>
                  {msg.body || msg.title}
                </p>
              </div>
            </motion.div>
          ))
        )}
      </section>

      {/* Mark Attendance Modal */}
      {showMarkModal && (
        <div style={styles.modalOverlay} onMouseDown={(e) => {
          if (e.target === e.currentTarget) {
            setShowMarkModal(false);
          }
        }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            style={styles.modalContent}
          >
            <h3 style={styles.modalTitle}>בחר ילד לסימון הגעה</h3>
            
            {children
              .filter(c => !c.arrivedAt && c.isExpectedToday)
              .map((child) => (
                <motion.div
                  key={child.id}
                  style={{
                    ...styles.modalChildItem,
                    borderColor: selectedChild?.id === child.id ? '#14b8a6' : '#e5e7eb',
                    backgroundColor: selectedChild?.id === child.id ? '#f0fdfa' : 'white',
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedChild(child)}
                >
                  <ChildAvatar
                    photoUrl={child.photoUrl}
                    firstName={child.firstName}
                    lastName={child.lastName}
                    size={48}
                    borderRadius={12}
                  />
                  <div>
                    <div style={{ fontWeight: '600', color: '#1f2937' }}>
                      {child.firstName} {child.lastName}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      {child.kindergartenName}
                    </div>
                  </div>
                </motion.div>
              ))}
            
            <button
              style={{
                ...styles.modalButton,
                backgroundColor: selectedChild ? '#14b8a6' : '#d1d5db',
                color: 'white',
              }}
              disabled={!selectedChild || marking}
              onClick={() => selectedChild && markChildArrival(selectedChild)}
            >
              {marking ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <Loader2 className="animate-spin" size={20} />
                  מסמן...
                </span>
              ) : (
                'סמן הגעה'
              )}
            </button>
            
            <button
              style={{
                ...styles.modalButton,
                backgroundColor: 'transparent',
                color: '#6b7280',
                border: '1px solid #e5e7eb',
              }}
              onClick={() => setShowMarkModal(false)}
            >
              ביטול
            </button>
          </motion.div>
        </div>
      )}

      {/* Message Modal */}
      <AnimatePresence>
        {messageModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                closeMessage();
              }
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
              padding: '20px',
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{
                backgroundColor: 'white',
                borderRadius: '20px',
                padding: '24px',
                width: '100%',
                maxWidth: '320px',
                textAlign: 'center',
              }}
            >
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                backgroundColor: messageModal.type === 'success' ? modalColors.successLight :
                                messageModal.type === 'error' ? modalColors.dangerLight :
                                messageModal.type === 'warning' ? modalColors.warningLight :
                                modalColors.primaryLight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                {messageModal.type === 'success' && <CheckCircle2 size={28} color={modalColors.success} />}
                {messageModal.type === 'error' && <XCircle size={28} color={modalColors.danger} />}
                {messageModal.type === 'warning' && <AlertTriangle size={28} color={modalColors.warning} />}
                {messageModal.type === 'info' && <Bell size={28} color={modalColors.primary} />}
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: modalColors.text, margin: '0 0 8px 0' }}>
                {messageModal.title}
              </h3>
              <p style={{ fontSize: '14px', color: modalColors.textLight, margin: '0 0 20px 0', lineHeight: '1.5' }}>
                {messageModal.message}
              </p>
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={closeMessage}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '12px',
                  backgroundColor: messageModal.type === 'success' ? modalColors.success :
                                  messageModal.type === 'error' ? modalColors.danger :
                                  messageModal.type === 'warning' ? modalColors.warning :
                                  modalColors.primary,
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                אישור
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Child Status Card Component
function ChildStatusCard({
  child,
  index,
  onClick
}: {
  child: ChildData;
  index: number;
  onClick?: () => void;
}) {
  const hasArrived = !!child.arrivedAt;
  const isOnVacation = child.isOnVacationToday;
  const vacationLabel = child.vacationReason === 2 ? 'מחלה' : 'חופשה';

  // Colors: arrived=green, vacation=blue, pending=yellow
  const borderColor = hasArrived ? '#22c55e' : isOnVacation ? '#3b82f6' : '#f59e0b';
  const bgColor = hasArrived ? '#dcfce7' : isOnVacation ? '#dbeafe' : '#fef3c7';
  const textColor = hasArrived ? '#15803d' : isOnVacation ? '#1d4ed8' : '#92400e';
  const avatarBorder = hasArrived ? '#bbf7d0' : isOnVacation ? '#93c5fd' : '#fde68a';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      style={{
        ...styles.childCard,
        borderTop: `3px solid ${borderColor}`,
      }}
      onClick={onClick}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <ChildAvatar
        photoUrl={child.photoUrl}
        firstName={child.firstName}
        lastName={child.lastName}
        size={40}
        borderRadius={12}
        borderWidth={2}
        borderColor={avatarBorder}
      />

      <div style={styles.childInfo}>
        <h4 style={styles.childName}>{child.firstName}</h4>
        <p style={styles.childKindergarten}>{child.kindergartenName}</p>
      </div>

      <div style={{
        ...styles.statusBadge,
        backgroundColor: bgColor,
        color: textColor,
      }}>
        {hasArrived ? (
          <>
            <CheckCircle2 size={12} />
            <span>{child.arrivedAt}</span>
          </>
        ) : isOnVacation ? (
          <>
            <Clock size={12} />
            <span>{vacationLabel}</span>
          </>
        ) : (
          <>
            <Clock size={12} />
            <span>ממתין</span>
          </>
        )}
      </div>
    </motion.div>
  );
}

// Action Button Component
function ActionButton({ 
  icon: Icon, 
  label, 
  bgColor, 
  iconColor, 
  onClick,
  disabled = false
}: { 
  icon: React.ElementType;
  label: string;
  bgColor: string;
  iconColor: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <motion.button
      onClick={onClick}
      disabled={disabled}
      style={{
        ...styles.actionCard,
        backgroundColor: bgColor,
        color: iconColor,
        opacity: disabled ? 0.6 : 1,
      }}
      whileHover={disabled ? {} : { scale: 1.02 }}
      whileTap={disabled ? {} : { scale: 0.98 }}
    >
      <Icon size={22} />
      <span style={styles.actionLabel}>{label}</span>
    </motion.button>
  );
}